import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import {
  GetSaleBills,
  DeleteSaleBill
} from "../../../../api/medicalStore/transaction/saleBillAPI";

const ListSaleBillEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  /* ================= FETCH DATA ================= */
  const fetchData = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetSaleBills();

      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,

          docType: item.docType || "",
          billNo: item.billNo || "",
          date: item.date
            ? new Date(item.date).toLocaleDateString()
            : "",
          time: item.time || "",

          customerType: item.customerType || "",
          customerName: item.customerName || "",
          organisation: item.organisation || "",

          opdEntryId: item.opdEntryId ? "OPD" : "-",
          ipdEntryId: item.ipdEntryId ? "IPD" : "-",

          department: item.departmentId?.name || "",
          consultant: item.consultantId?.name || "",

          totalQty: item.totalQty || 0,
          grossAmount: item.grossAmount || 0,
          gst: item.gst || 0,

          discountPercentage: item.discountPercentage || 0,
          discountAmount: item.discountAmount || 0,

          discountPercentageAfterGst:
            item.discountPercentageAfterGst || 0,
          discountAmountAfterGst:
            item.discountAmountAfterGst || 0,

          roundOff: item.roundOff || 0,
          netAmount: item.netAmount || 0,

          againstAccount: item.againstAccount || "",
          remark: item.remark || "",

          createdBy: item.createdBy?.name || "",
          createdAt: new Date(item.createdAt).toLocaleDateString()
        }));

        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message || "Something went wrong");
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ================= SEARCH ================= */
  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  /* ================= ACTIONS ================= */
  const handleEditClick = id => {
    navigate(
      `/medical-store/transaction/sale-bill-entry/edit/${id}`
    );
  };

  const handleDeleteClick = id => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will soft delete the sale bill!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeleteSaleBill(id);
          if (response.success) {
            showToast("success", response.message);
            setData(prev => prev.filter(item => item.id !== id));
            setFilteredData(prev => prev.filter(item => item.id !== id));
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  /* ================= TABLE COLUMNS ================= */
  const columns = [
    { name: "S.No", selector: row => row.index, width: "8%" },
    { name: "Bill No", selector: row => row.billNo, sortable: true, width: "10%" },
    { name: "Doc Type", selector: row => row.docType, width: "10%" },
    { name: "Date", selector: row => row.date, width: "10%" },
    { name: "Time", selector: row => row.time, width: "10%" },
    { name: "Customer Name", selector: row => row.customerName, width: "10%" },
    { name: "Customer Type", selector: row => row.customerType, width: "10%" },
    { name: "Organisation", selector: row => row.organisation, width: "10%" },
    { name: "Department", selector: row => row.department, width: "10%" },
    { name: "Consultant", selector: row => row.consultant, width: "10%" },
    { name: "Qty", selector: row => row.totalQty, width: "10%" },
    { name: "Gross", selector: row => row.grossAmount, width: "10%" },
    { name: "GST", selector: row => row.gst, width: "10%" },
    { name: "Disc %", selector: row => row.discountPercentage, width: "10%" },
    { name: "Disc Amt", selector: row => row.discountAmount, width: "10%" },
    {
      name: "Disc After GST",
      selector: row => row.discountAmountAfterGst, width: "10%"
    },
    { name: "Round Off", selector: row => row.roundOff, width: "10%" },
    { name: "Net Amount", selector: row => row.netAmount, width: "10%" },
    { name: "Against A/c", selector: row => row.againstAccount, width: "10%" },
    { name: "Remark", selector: row => row.remark, width: "10%" },
    { name: "Created At", selector: row => row.createdAt, width: "10%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: "15%"
    }
  ];

  /* ================= CSV ================= */
  const headers = Object.keys(data[0] || {}).map(key => ({
    label: key.toUpperCase(),
    key
  }));

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="sale-bills.csv">
      <button className="btn btn-primary">Export</button>
    </CSVLink>
  );

  /* ================= UI ================= */
  return (
    <Fragment>
      <Breadcrumb parent="Medical Store" title="Sale Bills" />

      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between">
              <h5>Sale Bill List</h5>
              <Link
                to={`/medical-store/transaction/sale-bill-entry/add`}
              >
                <button className="btn btn-primary">
                  Create Sale Bill
                </button>
              </Link>
            </div>

            <div className="row mt-2">
              <div className="col-sm-6">
                <input
                  className="form-control"
                  placeholder="Search Sale Bill..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
              <div className="col-sm-6 text-end">
                <ExportCSV data={data} />
              </div>
            </div>
          </div>

          <div className="card-body">
            <DataTable
              columns={columns}
              data={filteredData}
              pagination
              highlightOnHover
              dense
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListSaleBillEntry;
